import { Check, Copy } from "lucide-react";
import { useRef, useState } from "react";

export interface CopyButtonContent {
  html: string;
  text: string;
}

interface CopyButtonProps {
  getContent: () => CopyButtonContent;
  ariaLabel: string;
  title?: string;
  className?: string;
  size?: number;
  withLabel?: boolean;
}

const TOOLBAR_CLASS =
  "flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover";

async function writeRichText({ html, text }: CopyButtonContent): Promise<void> {
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return;
    } catch {
      // fall through to writeText
    }
  }
  await navigator.clipboard.writeText(text);
}

export function CopyButton({
  getContent,
  ariaLabel,
  title,
  className,
  size = 14,
  withLabel = false,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = async () => {
    const content = getContent();
    try {
      await writeRichText(content);
    } catch {
      return;
    }
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const label = copied ? "Copied" : ariaLabel;
  const buttonClass = className ?? TOOLBAR_CLASS;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={buttonClass}
      aria-label={label}
      title={title ?? ariaLabel}
    >
      {copied ? <Check size={size} /> : <Copy size={size} />}
      {withLabel && <span>{copied ? "Copied" : "Copy"}</span>}
    </button>
  );
}
